import { Strings } from "../engine/strings";
import type { GameStats } from "../engine/gameStats";
import { createStatsPanel } from "./statsDisplay";
import { StatsViewModel } from "./viewModels/statsViewModel";
import { createOverlay, createButton } from "./dom";
import { playUiSound, UiSound } from "./sound/uiSoundPlayer";

export function createSeedRow(seed: string): HTMLElement {
	const seedRow = document.createElement("div");
	seedRow.className = "seed-row";

	const seedLabel = document.createElement("span");
	seedLabel.textContent = "Seed: ";

	const seedValue = document.createElement("span");
	seedValue.className = "seed-value";
	seedValue.textContent = seed;

	const copyBtn = document.createElement("button");
	copyBtn.className = "seed-copy-btn";
	copyBtn.textContent = Strings.get("ui_btnCopySeed") ?? "Copy";
	copyBtn.onclick = () => {
		navigator.clipboard.writeText(seed).then(() => {
			copyBtn.textContent = Strings.get("ui_labelCopied") ?? "Copied!";
			setTimeout(() => {
				copyBtn.textContent = Strings.get("ui_btnCopySeed") ?? "Copy";
			}, 2000);
		});
	};

	seedRow.appendChild(seedLabel);
	seedRow.appendChild(seedValue);
	seedRow.appendChild(copyBtn);
	return seedRow;
}

export function showGameOverOverlay(seed: string, stats: GameStats | null, onNewGame: () => void): void {
	playUiSound(UiSound.GAME_OVER);
	const overlay = createOverlay({ level: 1 });
	overlay.panel.classList.add("bare");

	const title = document.createElement("div");
	title.className = "modal-title danger huge";
	title.textContent = Strings.get("ui_gameOverLabel") ?? "GAME OVER";

	const newGameBtn = createButton(
		Strings.get("ui_btnNewGame") ?? "New Game",
		() => {
			overlay.close();
			onNewGame();
		},
		"primary",
	);
	newGameBtn.classList.add("btn-wide");

	const seedRow = createSeedRow(seed);
	seedRow.style.marginBottom = "10px";

	overlay.panel.appendChild(title);

	if (stats) {
		const data = new StatsViewModel().computePanelData(stats);
		const statsPanel = createStatsPanel(data);
		statsPanel.classList.add("in-overlay");
		overlay.panel.appendChild(statsPanel);
	}

	overlay.panel.appendChild(seedRow);
	overlay.panel.appendChild(newGameBtn);
}

export function showVictoryOverlay(
	seed: string,
	stats: GameStats | null,
	onKeepPlaying: () => void,
	onNewGame: () => void,
): void {
	const overlay = createOverlay({ level: 1 });
	overlay.panel.classList.add("bare");

	const title = document.createElement("div");
	title.className = "modal-title victory huge";
	title.textContent = Strings.get("ui_labelVictory") ?? "VICTORY!";

	const message = document.createElement("div");
	message.className = "modal-subtitle large";
	message.textContent = Strings.get("ui_labelVictoryMessage") ?? "You have completed the game!";

	const keepPlayingBtn = createButton(
		Strings.get("ui_btnKeepPlaying") ?? "Keep Playing",
		() => {
			overlay.close();
			onKeepPlaying();
		},
		"primary",
	);
	keepPlayingBtn.classList.add("btn-wide");

	const newGameBtn = createButton(
		Strings.get("ui_btnNewGame") ?? "New Game",
		() => {
			overlay.close();
			onNewGame();
		},
		"primary",
	);
	newGameBtn.classList.add("btn-wide");

	const seedRow = createSeedRow(seed);
	seedRow.style.marginBottom = "10px";

	overlay.panel.appendChild(title);
	overlay.panel.appendChild(message);

	if (stats) {
		const data = new StatsViewModel().computePanelData(stats);
		const statsPanel = createStatsPanel(data);
		statsPanel.classList.add("in-overlay");
		overlay.panel.appendChild(statsPanel);
	}

	overlay.panel.appendChild(seedRow);
	overlay.panel.appendChild(keepPlayingBtn);
	overlay.panel.appendChild(newGameBtn);
}

export function showCrashOverlay(error: unknown, onReturnToMenu: () => void): void {
	const overlay = createOverlay({ level: 1 });
	overlay.panel.classList.add("bare");

	const title = document.createElement("div");
	title.className = "modal-title danger";
	title.textContent = "Game crashed";

	const message = document.createElement("div");
	message.className = "modal-subtitle message";
	message.textContent = error instanceof Error ? error.message : String(error);

	const quitBtn = createButton(
		"Return to Menu",
		() => {
			overlay.close();
			onReturnToMenu();
		},
		"primary",
	);
	quitBtn.classList.add("btn-wide");
	quitBtn.style.marginTop = "20px";

	overlay.panel.appendChild(title);
	overlay.panel.appendChild(message);
	overlay.panel.appendChild(quitBtn);
}

/** Escape is handled by the game's input handler so the pause toggle isn't double-registered. */
export function showPauseMenu(options: {
	seed: string;
	stats?: GameStats | null;
	onResume: () => void;
	onSettings: () => void;
	onQuit: () => void;
}): HTMLElement {
	const overlay = createOverlay({ level: 1 });
	overlay.element.classList.add("muted");

	const title = document.createElement("div");
	title.className = "modal-title";
	title.textContent = Strings.get("ui_labelPaused") ?? "Paused";

	const resumeBtn = createButton(
		Strings.get("ui_btnResume") ?? "Resume",
		() => options.onResume(),
		"primary",
	);
	resumeBtn.classList.add("btn-wide");

	const settingsBtn = createButton(
		Strings.get("ui_btnSettings") ?? "Settings",
		() => options.onSettings(),
		"secondary",
	);
	settingsBtn.classList.add("btn-wide");

	const quitBtn = createButton(
		Strings.get("ui_btnQuitToMenu") ?? "Quit to Menu",
		() => options.onQuit(),
		"danger",
	);
	quitBtn.classList.add("btn-wide");

	overlay.panel.appendChild(title);
	overlay.panel.appendChild(resumeBtn);
	overlay.panel.appendChild(settingsBtn);
	overlay.panel.appendChild(quitBtn);

	if (options.stats) {
		const data = new StatsViewModel().computePanelData(options.stats);
		const statsPanel = createStatsPanel(data);
		statsPanel.classList.add("in-pause");
		overlay.panel.appendChild(statsPanel);
	}

	const seedRow = createSeedRow(options.seed);
	seedRow.style.marginTop = "12px";
	overlay.panel.appendChild(seedRow);

	return overlay.element;
}

/** Stacks above other modals. Escape cancels. */
export function showConfirmDialog(message: string, onConfirm: () => void): void {
	const overlay = createOverlay({
		level: 2,
		onEscape: () => overlay.close(),
	});
	overlay.element.classList.add("translucent");
	overlay.panel.classList.add("compact");

	const confirmText = document.createElement("div");
	confirmText.className = "modal-subtitle confirm";
	confirmText.textContent = message;

	const btnRow = document.createElement("div");
	btnRow.className = "btn-row";

	const yesBtn = createButton(
		"Yes",
		() => {
			overlay.close();
			onConfirm();
		},
		"primary",
	);
	yesBtn.classList.add("btn-compact");

	const noBtn = createButton(
		"No",
		() => overlay.close(),
		"secondary",
	);
	noBtn.classList.add("btn-compact");

	btnRow.appendChild(yesBtn);
	btnRow.appendChild(noBtn);
	overlay.panel.appendChild(confirmText);
	overlay.panel.appendChild(btnRow);
}
